import type {Block, ComponentDefinition} from 'grapesjs';
import React from 'react';
import type {ReactElement} from 'react';
import {Text, TextSize} from '@Components/text';
import {EmailMakerBlock} from '@Components/email-maker-block-manager/components/email-maker-block';
import {getUniqueString} from '@Utils/string.util';
import {Image} from 'react-bootstrap';
import type {CategoryPill, EmailComponentTemplate} from '@Components/email-maker-block-manager/email-maker-block-manager.types';
import {getAllCategoriesPill} from '@Libraries/email-component-template-library';
import {useGrapesjsEditor, useIsMobileScreen} from '@Components/email-maker-block-manager/email-maker-block-manager-grapesjs-editor-context';
import {useSortedCategories, useAllComponentTemplates} from '@Components/email-maker-block-manager/email-maker-block-manager.hooks';
import styles from './email-component-templates.module.scss';

interface EmailComponentTemplatesProps {
  selectedCategoryId: number;
  headerLabelOverride?: string;
}

const ADMIN_COMPONENT_TEMPLATE_USER_ID = '0';

export function EmailComponentTemplates({headerLabelOverride, selectedCategoryId = 0}: EmailComponentTemplatesProps): ReactElement<any> | null {
  const gjsEditor = useGrapesjsEditor();
  const isMobileScreen = useIsMobileScreen();
  const allTemplates = useAllComponentTemplates();
  const allSortedCategories = useSortedCategories();

  const createBlockId = (template: EmailComponentTemplate): string => {
    return `${ADMIN_COMPONENT_TEMPLATE_USER_ID}_${template.id.toString()}`;
  };

  const getTemplateBlocks = (templates: EmailComponentTemplate[]): Block[] => {
    const blocks = templates.map((template): Block => {
      return gjsEditor.BlockManager.add(createBlockId(template), {
        activate: true,
        label: '',
        select: true,
        content: JSON.parse(template.jsonContent) as ComponentDefinition,
      });
    });

    blocks.forEach((block) => {
      gjsEditor.BlockManager.remove(block.getId());
    });

    return blocks;
  };

  const getPanelHeader = (categoryId: number): ReactElement<any> | null => {
    if (headerLabelOverride) {
      return <Text val={headerLabelOverride} size={TextSize.SMALL} bold className={styles.panelHeader} />;
    }

    const categoryName = allSortedCategories.find((category: CategoryPill) => category.id === categoryId)?.name;

    if (selectedCategoryId === getAllCategoriesPill().id && categoryName) {
      return <Text val={categoryName} size={TextSize.SMALL} bold className={styles.panelHeader} />;
    }

    return null;
  };

  const filterTemplateComponentsByCategoryId = (templates: EmailComponentTemplate[], categoryId: number): EmailComponentTemplate[] => {
    return templates.filter((template: EmailComponentTemplate) => {
      return template.categoryId === categoryId;
    });
  };

  const sortTemplateComponentsBySortOrder = (templates: EmailComponentTemplate[]): EmailComponentTemplate[] => {
    return templates.sort((a, b) => {
      return a.sortOrder - b.sortOrder;
    });
  };

  const getEmailComponentTemplateVisual = (componentTemplate: EmailComponentTemplate): ReactElement<any> => {
    const sourceUrl = isMobileScreen ? componentTemplate.mobilePreviewUrl : componentTemplate.desktopPreviewUrl;

    return componentTemplate.hasGif ? (
      <video src={sourceUrl} className={styles.blockVisual} muted autoPlay playsInline loop />
    ) : (
      <Image src={sourceUrl} className={styles.blockVisual} alt={window.i18next.t('pmwjs_image')} />
    );
  };

  const getTemplateComponentsByCategoryId = (categoryId: number): ReactElement<any> | null => {
    const templatesToDisplay = filterTemplateComponentsByCategoryId(allTemplates, categoryId);

    if (templatesToDisplay.length === 0) {
      return null;
    }

    const sortedTemplatesToDisplay = sortTemplateComponentsBySortOrder(templatesToDisplay);
    const templateBlocks = getTemplateBlocks(sortedTemplatesToDisplay);

    return (
      <div className={styles.componentsSection} key={getUniqueString()}>
        {getPanelHeader(categoryId)}
        <div className={styles.templatesContainer}>
          {templateBlocks.map((block, index) => {
            return (
              <EmailMakerBlock block={block} extraClasses={styles.block} key={getUniqueString()}>
                {getEmailComponentTemplateVisual(templatesToDisplay[index])}
              </EmailMakerBlock>
            );
          })}
        </div>
      </div>
    );
  };

  const getAllTemplateComponents = (): ReactElement<any> => {
    return (
      <div className={styles.allComponentsSection}>
        {allSortedCategories.map((category) => {
          return getTemplateComponentsByCategoryId(category.id);
        })}
      </div>
    );
  };

  const getTemplateComponents = (): ReactElement<any> | null => {
    if (allTemplates.length === 0) {
      return null;
    }

    return selectedCategoryId === getAllCategoriesPill().id ? getAllTemplateComponents() : getTemplateComponentsByCategoryId(selectedCategoryId);
  };

  return getTemplateComponents();
}
